import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoMdNotificationsOutline, IoMdMail, IoMdLogOut, IoMdMenu } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import UserAvater from "../../Assets/images/user-avater.png";
import Logo from "../../Assets/images/ConnectHealthPro.png";
import { alpha, Avatar, Box, createTheme, Divider, InputBase, Typography, useMediaQuery, useTheme } from "@mui/material";
import { green } from "@mui/material/colors";
import {
  Feedback,
  Notifications,
  Password,
  Search as SearchIcon,
  // For mobile menu
} from '@mui/icons-material';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  styled,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { put as puts } from '../../utility/fetchWeb';
import { post, put, get } from '../../utility/fetch';

import notification from '../../utility/notification';
import UploadButton from './UploadButton';
import PasswordResetModal from '../PasswordResetModal';
import PatientSearchSelect from "./PatientSearch";
import { UserContext } from '../../contexts/UserContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { useFirebaseMessaging } from '../../contexts/FirebaseMessagingContext';

const feedbackurl = process.env.REACT_APP_REPORT_URL
// Define a theme for consistent styling
const theme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32', // Green color for Play Game buttons and other primary actions
      dark: green[800], // Darker green for AppBar background
    },
    secondary: {
      main: '#f5f5f5', // Light grey for background elements
    },
    grey: {
      200: '#eeeeee',
      300: '#e0e0e0',
      700: '#616161',
      800: '#424242', // Darker grey for text on light backgrounds
    },
    background: {
      default: '#f7f9fc', // Page background
      paper: '#ffffff', // Card backgrounds
    },
    text: {
      primary: '#333333',
      secondary: '#555555',
      disabled: '#9e9e9e',
    }
  },
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    body1: {
      fontWeight: 500,
    },
    body2: {
      color: '#555',
    },
    caption: {
      color: alpha('#ffffff', 0.8), // Lighter text for subtitle in AppBar
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          textTransform: 'none',
          padding: '6px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          transition: 'border-color 0.2s ease-in-out',
          '&:hover': {
            borderColor: '#bdbdbd',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none', // Flat design for AppBar
        }
      }
    }
  },
});

const ProfileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 380,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    zIndex: 1099,
    background: 'linear-gradient(to bottom, #ffffff, #f8fafc)',
    borderLeft: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '-4px 0 16px rgba(0,0,0,0.05)',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#E0E0E0',
      borderRadius: '4px',
      '&:hover': {
        background: '#BDBDBD',
      },
    },
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DrawerSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiDivider-root': {
    margin: theme.spacing(2, 0),
  }
}));

const UserInfoContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: theme.spacing(3),
  background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
  border: '1px solid rgba(238, 238, 238, 0.04)',
  boxShadow: '-4px 0 16px rgba(0,0,0,0.05)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 100,
    background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, #ffffff 100%)',
    pointerEvents: 'none',
    zIndex: -1,
  }
}));

const Header = () => {

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [requestingId, setRequestingId] = useState(null);
  const [sentIds, setSentIds] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const { profilePicture, setProfilePicture } = useContext(UserContext);
  
  // Replace FirebaseMessagingContext usage with custom hook
  const { 
    unreadCount, 
    messages, 
    markAllAsRead, 
    isInitialized, 
    initError 
  } = useFirebaseMessaging();

  const navigate = useNavigate();

  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Adjusted breakpoint for header items

  const token = sessionStorage.getItem("token");

  // If no token, don't render anything
  if (!token) {
    return null;
  }

  const handleLogout = async () => {
    try {
      const userId = sessionStorage.getItem("userId");

      // Disconnect user presence before logging out
      if (userId) {
        await post(`/UserPresence/disconnect/${userId}`, {
          userId: Number(userId),
        });
      }
    } catch (error) {
      console.error("Error disconnecting user presence:", error);
      // Continue with logout even if presence disconnect fails
    } finally {
      // Clear session data and navigate to login
      localStorage.removeItem("USER_INFO");
      sessionStorage.clear(); // Clear all session data
      navigate("/login");
    }
  };

  const toggleMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const handleNavigate = (path) => {
    setShowMobileMenu(false);
    navigate(path);
  };

  // Replace the password reset navigation
  const handlePasswordReset = () => {
    setPasswordResetOpen(true);
  };




  const handleSendRequest = async (id, isHealthPractitioner) => {
    const userId = Number(sessionStorage.getItem('userId'));
    const isSenderHealthPractitioner = true;
    setRequestingId(id);

    const headers = {
      Accept: 'application/json',
    };

    const token = sessionStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const payload = {
        senderPatientId: isSenderHealthPractitioner ? 0 : userId,
        senderEmployeeId: isSenderHealthPractitioner ? userId : 0,
        isSenderHealthPractitioner: true,
        friendRequests: [
          {
            receiverPatientId: isHealthPractitioner ? 0 : id,
            receiverEmployeeId: isHealthPractitioner ? id : 0,
            isReceiverHealthPractitioner: isHealthPractitioner,
          },
        ],
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/friendrequest/create-friend-request`,
        payload,
        { headers }
      );

      if (res?.data?.statusCode === 200) {
        notification({
          type: 'success',
          message: 'Friend request sent successfully',
        });
        setSentIds((prev) => [...prev, id]);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.errorData?.[0] ||
        error.response?.data?.message ||
        'Failed to send friend request';
      notification({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setRequestingId(null);
    }
  };



  return (
    <>
      <header style={{ backgroundColor: "#2e7d32" }} className="header-content">
        <div className="header-container">
          <div className="w-60">
            <PatientSearchSelect
              selectedPatientId={null}
              setSelectedPatientId={() => { }}
              sentIds={sentIds}
              requestingId={requestingId}
              handleSendRequest={handleSendRequest}
              isMobile={isMobile}
              theme={theme}
              alpha={alpha}
            />
          </div>

          <div className="right-icons">
            <IconButton 
              onClick={() => { 
                navigate("/notifications"); 
                markAllAsRead(); 
              }}
              sx={{
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Badge 
                badgeContent={unreadCount || 0} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#ff1744',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    minWidth: '16px',
                    height: '16px',
                    animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        transform: 'scale(1)',
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                      },
                    }
                  }
                }}
              >
                <Notifications sx={{ color: 'white' }} />
              </Badge>
              
            </IconButton>

            {initError && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: '100%', 
                  right: 0, 
                  mt: 1,
                  p: 1,
                  backgroundColor: 'error.main',
                  color: 'white',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  maxWidth: 200,
                  zIndex: 1000
                }}
              >
                {initError}
              </Box>
            )}

            <Box className="flex">
              <Box>
                <Typography color="white">{sessionStorage?.getItem("name")}</Typography>
                <Typography variant="body2" color="white">{sessionStorage?.getItem("role")}</Typography>
              </Box>
              <IconButton onClick={() => setDrawerOpen(true)}>
                <Avatar
                  src={profilePicture || UserAvater}
                  sx={{
                    ml: 2,
                    width: 40,
                    height: 40,
                    border: '2px solid white'
                  }}
                />
              </IconButton>
            </Box>
          </div>
        </div>

        {/* Add the Profile Drawer */}
        <ProfileDrawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          {/* Add a fixed header section */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              pt: 12,
              pb: 2,
              px: 1,
              borderBottom: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.08)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>

          {/* Main content with proper padding */}
          <Box
            sx={{
              height: '100%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              pt: 3, // Add top padding for content
              pb: 4
            }}
          >
            <UserInfoContainer>
              <Box sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -4,
                  left: -4,
                  right: -4,
                  bottom: -4,
                  borderRadius: '50%',
                  opacity: 0.1,
                  zIndex: 0
                }
              }}>
                <Avatar
                  src={profilePicture || UserAvater}
                  sx={{
                    width: 130,
                    height: 130,
                    border: '4px solid white',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1,
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 6px 32px rgba(0,0,0,0.15)',
                    }
                  }}
                />
                <UploadButton
                  buttonText={'Upload Photo'} // Optional: use camera icon for clarity
                  acceptedFiles="image/*"
                  onUploadSuccess={async (response) => {
                    if (response?.doclink) {
                      setProfilePicture(response?.doclink);
                      sessionStorage.setItem('profilePicture', response?.doclink);
                      const updateRes = await puts(`/Patient/photo-update-employee`, {
                        pictureUrl: response.doclink,
                        employeeId: Number(sessionStorage.getItem('userId'))
                      });
                    }
                  }}
                  onUploadError={(error) => {
                    notification({
                      type: 'error',
                      message: 'Failed to update profile photo'
                    });
                  }}
                />

              </Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #1b5e20, #2e7d32)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {sessionStorage.getItem('name')}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  py: 0.5,
                  px: 2.5,
                  borderRadius: 6,
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                  background: 'linear-gradient(to right, #f1f5f9, #ffffff)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
                  gap: 1
                }}
              >
                <span style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#2e7d32',
                  display: 'inline-block'
                }} />
                {sessionStorage.getItem('role')}
              </Typography>
            </UserInfoContainer>

            <List sx={{
              mt: 2,
              borderRadius: 3,
              background: 'linear-gradient(-95deg,#f5f7fa 20%, #ffffff 100%)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
              p: 1,
              flex: 1 // Take remaining space
            }}>
              <ListItem
                button
                onClick={() => navigate('/health-record')}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                    transform: 'translateX(4px)',
                    '& .MuiListItemIcon-root': {
                      transform: 'scale(1.1)',
                    }
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <ListItemIcon sx={{ transition: 'transform 0.2s ease' }}>
                  <AccountCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="My Profile"
                  secondary="View and edit your profile"
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '0.95rem'
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.8rem'
                  }}
                />
              </ListItem>
              <ListItem
                button
                onClick={handlePasswordReset}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                    transform: 'translateX(4px)',
                    '& .MuiListItemIcon-root': {
                      transform: 'scale(1.1)',
                    }
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <ListItemIcon sx={{ transition: 'transform 0.2s ease' }}>
                  <Password color="primary" />
                </ListItemIcon>


                <ListItemText
                  primary="Password Reset"
                  secondary="Change your password"
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '0.95rem'
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.8rem'
                  }}
                />
              </ListItem>


              <Divider sx={{ my: 2, opacity: 0.6 }} />
              <ListItem
                button
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: '#fff5f5',
                    transform: 'translateX(4px)',
                    '& .MuiListItemIcon-root': {
                      transform: 'scale(1.1)',
                    }
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <ListItemIcon sx={{ transition: 'transform 0.2s ease' }}>
                  <LogoutIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  secondary="Sign out of your account"
                  primaryTypographyProps={{
                    color: 'error.main',
                    fontWeight: 600,
                    fontSize: '0.95rem'
                  }}
                  secondaryTypographyProps={{
                    color: 'error.light',
                    fontSize: '0.8rem'
                  }}
                />
              </ListItem>
            </List>
          </Box>
        </ProfileDrawer>
      </header>

      <PasswordResetModal
        open={passwordResetOpen}
        onClose={() => setPasswordResetOpen(false)}
      />
    </>
  );
};

export default Header;
